import Head from 'next/head';
import Header from '../components/Header';
import Hero from '../components/Hero';
import Footer from '../components/Footer';
import { generateWebPageSchema } from '../utils/generateWebPageSchema';

export default function PrivacyPolicy() {

  const base = 'https://modernbuildremodel.com';
  const pageUrl = `${base}/privacy-policy`;

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": base },
      { "@type": "ListItem", "position": 2, "name": "Privacy Policy", "item": pageUrl }
    ]
  };

  return (
    <>
		<Head>
		  <title>Privacy Policy | Modern Build</title>
		  <meta name="title" content="Privacy Policy | Modern Build" />
		  <meta
		    name="description"
		    content="Read how Modern Build collects, uses, and protects your data. We use Google Analytics, reCAPTCHA, and secure systems to ensure your privacy online."
		  />
		  <meta name="author" content="Modern Build Home Remodeling" />
		  <meta name="keywords" content="privacy policy, data protection, personal information, California privacy rights, Google Analytics, Modern Build, reCAPTCHA" />
		  <link rel="canonical" href="https://modernbuildremodel.com/privacy-policy" />

		  {/* Open Graph */}
		  <meta property="og:title" content="Privacy Policy | Modern Build" />
		  <meta property="og:description" content="Your privacy matters. Learn how we securely handle your data, including use of analytics and reCAPTCHA to protect your browsing experience." />
		  <meta property="og:image" content="https://modernbuildremodel.com/hero/best-home-remodling-contractor-in-los-angeles.webp" />
		  <meta property="og:url" content="https://modernbuildremodel.com/privacy-policy" />
		  <meta property="og:type" content="website" />
		  <meta property="og:site_name" content="Modern Build Home Remodeling" />

		  {/* Twitter Card */}
		  <meta name="twitter:card" content="summary_large_image" />
		  <meta name="twitter:title" content="Privacy Policy | Modern Build" />
		  <meta name="twitter:description" content="See how Modern Build protects your privacy and uses tools like Google Analytics and reCAPTCHA to improve your experience." />
		  <meta name="twitter:image" content="https://modernbuildremodel.com/hero/best-home-remodling-contractor-in-los-angeles.webp" />
		  <meta name="twitter:site" content="@modernbuildhome" />

		  {/* JSON-LD: WebPage */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              generateWebPageSchema({
                title: 'Privacy Policy | Modern Build',
                description: 'Learn how Modern Build collects, uses, and protects your personal data on our website.',
                slug: '/privacy-policy'
              })
            )
          }}
        />

        {/* JSON-LD: Breadcrumb */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      </Head>

      <Header />
      <Hero category="privacyPolicy" page="page" hasContentH1={true}/>
      <div className="container mx-auto px-4 py-10 max-w-4xl">
<div className="prose prose-lg max-w-none text-md text-black">
  <h1 className="text-3xl font-gilda mb-6">Privacy Policy</h1>

  <p className="mb-4">
    At Modern Build Home Remodeling, we are committed to protecting your privacy and the security of your personal information. This policy explains how we collect, use, and safeguard the data you share with us.
  </p>

  <h2 className="text-xl font-semibold mt-6 mb-2">1. Information We Collect</h2>

  <h3 className="font-semibold mt-4">1.1 Personal Information</h3>
  <p className="mb-4">
    We may collect personal details such as your name, email address, phone number, project location, and any project notes you provide when you:
  </p>
  <ul className="list-disc pl-6 space-y-2 mb-4">
    <li>Submit a contact or estimate request form</li>
    <li>Communicate with our team via email or phone</li>
    <li>Subscribe to any of our newsletters or updates</li>
  </ul>

  <h3 className="font-semibold mt-4">1.2 Automatically Collected Data</h3>
  <p className="mb-4">We use analytics and security tools that automatically collect:</p>
  <ul className="list-disc pl-6 space-y-2 mb-4">
    <li>Your IP address and approximate location</li>
    <li>Browser type, device type, and operating system</li>
    <li>Pages visited and time spent on the website</li>
    <li>Interactions tracked via Google Analytics</li>
    <li>Bot and spam behavior monitoring via Google reCAPTCHA</li>
  </ul>

  <h2 className="text-xl font-semibold mt-6 mb-2">2. How We Use Your Information</h2>
  <ul className="list-disc pl-6 space-y-2 mb-4">
    <li>To respond to inquiries and provide remodeling estimates</li>
    <li>To connect you with independent licensed contractors at your request</li>
    <li>To improve user experience and website functionality</li>
    <li>To ensure security through spam and fraud prevention</li>
    <li>To send updates or marketing if you have opted in</li>
  </ul>

  <p className="mb-4">
    We may use collected information to create, improve, and distribute media content, including podcasts, interviews, and video programming. Any such content is provided for general informational and entertainment purposes only and does not constitute professional, legal, financial, or construction advice.
  </p>

  <h2 className="text-xl font-semibold mt-6 mb-2">3. Cookies and Tracking Technologies</h2>
  <p className="mb-4">
    We use cookies and similar tracking technologies to remember your preferences and to analyze
    how visitors interact with our site. You can disable cookies in your browser settings, however
    some features may not function properly.
  </p>

  <h3 className="font-semibold mt-4">3.1 Microsoft Clarity and Microsoft Advertising</h3>
  <p className="mb-4">
    We partner with Microsoft Clarity and Microsoft Advertising to capture how you use and interact
    with our website. This includes behavioral metrics, heatmaps, and session replay that help us
    understand which pages and content are most useful, how visitors move through the site, and
    where users may experience friction. Website usage data is captured using first and third party
    cookies and other tracking technologies and may be used to measure the popularity of content,
    improve our site design, support advertising, and help with fraud and security monitoring.
  </p>
  <p className="mb-4">
    By using this site, you agree that we and Microsoft may collect and use this information as
    described in this policy. For more details on how Microsoft collects and uses data, please see
    the{' '}
    <a
      href="https://www.microsoft.com/en-us/privacy/privacystatement"
      target="_blank"
      rel="noopener noreferrer"
      className="underline text-primary"
    >
      Microsoft Privacy Statement
    </a>
    .
  </p>

  <h2 className="text-xl font-semibold mt-6 mb-2">4. Sharing Your Information</h2>
  <p className="mb-4">
    We do not sell your personal information. We share it only as needed to deliver services or comply with legal obligations, including:
  </p>
  <ul className="list-disc pl-6 space-y-2 mb-4">
    <li>
      <span className="font-semibold">Referral to independent contractors.</span> If you request an estimate or service, we may share your contact details and project information with one or more independent licensed, bonded, and insured contractors in our network so they can contact you directly and provide quotes. These contractors are not our employees, they operate their own businesses.
    </li>
    <li>
      <span className="font-semibold">Service providers.</span> Analytics, security, hosting, and communication tools that process data on our behalf under contracts that limit their use of your information.
    </li>
    <li>
      <span className="font-semibold">Legal compliance.</span> When required by law, regulation, subpoena, or to protect rights and safety.
    </li>
  </ul>

  <h3 className="font-semibold mt-4">4.1 Clarification of Our Role</h3>
  <p className="mb-4">
    Modern Build Home Remodeling does not perform construction or remodeling work. All physical work is performed by independent contractors. We do not supervise or control contractor work, and we are not responsible for contractor performance, workmanship, permits, or compliance. Any service agreements, warranties, and payments are between you and the contractor you choose.
  </p>

  <h2 className="text-xl font-semibold mt-6 mb-2">5. Your Privacy Choices and Rights</h2>
  <ul className="list-disc pl-6 space-y-2 mb-4">
    <li>Request access to, correction of, or deletion of your personal data</li>
    <li>Request that we do not share your details with contractors, note that this may limit our ability to provide estimates</li>
    <li>California residents may exercise rights under the CCPA and CPRA</li>
    <li>To exercise your rights, email us at <a href="mailto:remodel@modernbuildremodel.com" className="underline">remodel@modernbuildremodel.com</a></li>
  </ul>

  <h2 className="text-xl font-semibold mt-6 mb-2">6. Data Security</h2>
  <p className="mb-4">
    We implement industry standard encryption, firewalls, and secure protocols to protect your data. No system is completely secure, we cannot guarantee absolute protection.
  </p>
  <p className="mb-4">
    All images and videos displayed on this website are generated using artificial intelligence and are not photographs or recordings of actual projects. They are provided for illustrative purposes only and should not be interpreted as real work performed by Modern Build or its contractors.
  </p>

  <h2 className="text-xl font-semibold mt-6 mb-2">7. Data Retention</h2>
  <p className="mb-4">
    We retain personal information only as long as necessary to fulfill the purposes described in this policy, to meet legal or accounting requirements, or as otherwise disclosed to you.
  </p>

  <h2 className="text-xl font-semibold mt-6 mb-2">8. Children’s Privacy</h2>
  <p className="mb-4">
    Our website is intended for adults. We do not knowingly collect personal information from children under 13.
  </p>

  <h2 className="text-xl font-semibold mt-6 mb-2">9. Changes to This Policy</h2>
  <p className="mb-4">
    We may update this Privacy Policy from time to time. All changes will be posted on this page with the latest update date.
  </p>

  <h2 className="text-xl font-semibold mt-6 mb-2">10. Contact Us</h2>
  <p className="mb-4">
    If you have questions or requests regarding your personal data, please contact us:
  </p>
  <ul className="list-disc pl-6 space-y-2 mb-4">
    <li>Phone: (323) 300-4130</li>
    <li>Email: <a href="mailto:remodel@modernbuildremodel.com" className="underline">remodel@modernbuildremodel.com</a></li>
    <li>Address: 4222 Longridge Ave, Studio City, CA 91604, USA</li>
  </ul>

  <p className="text-sm text-gray-600">Last updated: September 2025</p>
</div>


        </div>

      <Footer />
    </>
  );
}
